'use strict'

// Display formatting for number and text fields.
// The stored value stays raw. The browser formats a cell from these settings.
// A download uses the same functions when the server builds the file.

const { escapeHtml } = require('./html-sanitize')
const { readMultiselectItems } = require('./multiselect-display')

const CURRENCIES = {
  USD: { symbol: '$', position: 'prefix' },
  EUR: { symbol: '€', position: 'prefix' },
  GBP: { symbol: '£', position: 'prefix' },
  JPY: { symbol: '¥', position: 'prefix' },
  CAD: { symbol: 'CA$', position: 'prefix' },
  AUD: { symbol: 'A$', position: 'prefix' },
  CHF: { symbol: 'CHF', position: 'suffix' },
  CNY: { symbol: '¥', position: 'prefix' },
  INR: { symbol: '₹', position: 'prefix' },
  BRL: { symbol: 'R$', position: 'prefix' }
}

const FONT_FAMILIES = [
  'Arial, sans-serif',
  'Georgia, serif',
  '"Times New Roman", serif',
  '"Courier New", monospace',
  'Verdana, sans-serif'
]

const ROUNDING = ['round', 'floor', 'ceil']
const MAX_REPLACEMENTS = 20

const VALUE_FORMAT_OPTIONS = {

  number: {
    decimalPlaces: { type: 'number', label: 'Decimal Places', default: null, min: 0, max: 10 },
    rounding: { type: 'select', label: 'Rounding', default: null, options: [
      { value: null, label: 'Round' },
      { value: 'floor', label: 'Round down' },
      { value: 'ceil', label: 'Round up' }
    ]},
    thousandsSeparator: { type: 'select', label: 'Thousands Separator', default: null, options: [
      { value: null, label: 'None' },
      { value: ',', label: 'Comma (1,000)' },
      { value: '.', label: 'Period (1.000)' },
      { value: ' ', label: 'Space (1 000)' }
    ]},
    decimalSeparator: { type: 'select', label: 'Decimal Separator', default: '.', options: [
      { value: '.', label: 'Period (0.50)' },
      { value: ',', label: 'Comma (0,50)' }
    ]},
    currency: { type: 'select', label: 'Currency', default: null, options: [
      { value: null, label: 'None' },
      ...Object.keys(CURRENCIES).map((key) => ({ value: key, label: `${key} (${CURRENCIES[key].symbol})` }))
    ]},
    percentMode: { type: 'boolean', label: 'Display as Percentage', default: false }
  },

  text: {
    maxLength: { type: 'number', label: 'Max Display Length', default: null, min: 1 },
    stripHtml: { type: 'boolean', label: 'Strip HTML Tags', default: false },
    stripNonAlphanumeric: { type: 'boolean', label: 'Strip Non-Alphanumeric', default: false },
    trimWhitespace: { type: 'boolean', label: 'Trim Whitespace', default: false },
    caseTransform: { type: 'select', label: 'Case Transform', default: null, options: [
      { value: null, label: 'None' },
      { value: 'uppercase', label: 'UPPERCASE' },
      { value: 'lowercase', label: 'lowercase' },
      { value: 'capitalize', label: 'Capitalize Words' }
    ]},
    replacements: { type: 'list', label: 'Text replacements', default: [] },
    bold: { type: 'boolean', label: 'Bold', default: false },
    italic: { type: 'boolean', label: 'Italic', default: false },
    strike: { type: 'boolean', label: 'Strikethrough', default: false },
    fontFamily: { type: 'select', label: 'Font', default: null },
    fontSize: { type: 'number', label: 'Font size', default: null, min: 8, max: 96 }
  }
}

function decimalPlaces (value) {

  if (value === null || value === undefined || value === '') return null

  const places = Number(value)

  if (!Number.isFinite(places)) return null

  return Math.max(0, Math.min(10, places))
}

function scale (num, places, mode) {

  const factor = 10 ** places
  const scaled = num * factor
  let next = scaled

  if (mode === 'floor') next = Math.floor(scaled)
  else if (mode === 'ceil') next = Math.ceil(scaled)
  else next = Math.round(scaled)

  return next / factor
}

function applySeparators (numStr, thousandsSep, decimalSep) {

  const parts = String(numStr).split('.')
  let integerPart = parts[0]
  const decimalPart = parts[1] || null

  if (thousandsSep) {

    const isNegative = integerPart.startsWith('-')

    if (isNegative) integerPart = integerPart.slice(1)

    const digits = integerPart.split('').reverse()
    const groups = []

    for (let index = 0; index < digits.length; index += 1) {

      if (index > 0 && index % 3 === 0) groups.push(thousandsSep)

      groups.push(digits[index])
    }

    integerPart = groups.reverse().join('')

    if (isNegative) integerPart = `-${integerPart}`
  }

  if (decimalPart !== null) return `${integerPart}${decimalSep}${decimalPart}`

  return integerPart
}

function formatNumber (value, config) {

  let num = Number(value)

  if (Number.isNaN(num)) return String(value)

  const settings = config || {}

  if (settings.percentMode) num = num * 100

  const places = decimalPlaces(settings.decimalPlaces)
  const mode = ROUNDING.indexOf(settings.rounding) >= 0 ? settings.rounding : 'round'
  let formatted

  if (places === null) {

    if (mode === 'floor') num = Math.floor(num)
    else if (mode === 'ceil') num = Math.ceil(num)

    formatted = String(num)
  } else if (mode === 'round') {

    formatted = num.toFixed(places)
  } else {

    formatted = scale(num, places, mode).toFixed(places)
  }

  const decimalSep = settings.decimalSeparator || '.'
  const thousandsSep = settings.thousandsSeparator || null

  if (thousandsSep || decimalSep !== '.') formatted = applySeparators(formatted, thousandsSep, decimalSep)

  if (settings.currency && CURRENCIES[settings.currency]) {

    const currency = CURRENCIES[settings.currency]

    formatted = currency.position === 'prefix' ? `${currency.symbol}${formatted}` : `${formatted} ${currency.symbol}`
  }

  if (settings.percentMode) formatted = `${formatted}%`

  return formatted
}

function stripHtmlTags (str) {

  return String(str).replace(/<[^>]*>/g, '')
}

function normalizedReplacements (value) {

  if (!Array.isArray(value)) return []

  const replacements = []

  for (const entry of value) {

    if (!entry || replacements.length >= MAX_REPLACEMENTS) continue

    const find = String(entry.find == null ? '' : entry.find).slice(0, 80)

    if (!find) continue

    replacements.push({
      find,
      replace: String(entry.replace == null ? '' : entry.replace).slice(0, 80)
    })
  }

  return replacements
}

function applyReplacements (str, replacements) {

  let next = str

  for (const entry of replacements) {

    next = next.split(entry.find).join(entry.replace)
  }

  return next
}

function formatText (value, config) {

  const settings = config || {}
  let str = String(value)

  if (settings.stripHtml) str = stripHtmlTags(str)

  if (settings.stripNonAlphanumeric) str = str.replace(/[^a-zA-Z0-9\s]/g, '')

  if (settings.trimWhitespace) str = str.replace(/\s+/g, ' ').trim()

  if (settings.caseTransform === 'uppercase') str = str.toUpperCase()
  else if (settings.caseTransform === 'lowercase') str = str.toLowerCase()
  else if (settings.caseTransform === 'capitalize') str = str.replace(/\b\w/g, (letter) => letter.toUpperCase())

  str = applyReplacements(str, normalizedReplacements(settings.replacements))

  const maxLength = Number(settings.maxLength)

  if (Number.isFinite(maxLength) && maxLength > 0 && str.length > maxLength) {

    str = `${str.slice(0, maxLength)}…`
  }

  return str
}

function formatValue (value, config) {

  if (value === null || value === undefined) return value
  if (!config || !config.type) return value

  if (config.type === 'number') return formatNumber(value, config)
  if (config.type === 'text') return formatText(value, config)

  return value
}

function textStyle (config) {

  if (!config || config.type !== 'text') return null

  const style = {}

  if (config.bold) style.fontWeight = '700'
  if (config.italic) style.fontStyle = 'italic'
  if (config.strike) style.textDecoration = 'line-through'

  const size = Number(config.fontSize)

  if (Number.isFinite(size) && size >= 8 && size <= 96) style.fontSize = `${Math.round(size)}px`

  if (FONT_FAMILIES.indexOf(config.fontFamily) >= 0) style.fontFamily = config.fontFamily

  return Object.keys(style).length ? style : null
}

function formatFieldDisplay (value, config) {

  if (!config || (config.type !== 'number' && config.type !== 'text')) return null
  if (value === null || value === undefined || value === '') return null

  const text = formatValue(value, config)

  return {
    text: text == null ? '' : String(text),
    style: textStyle(config)
  }
}

function styleAttribute (style) {

  if (!style) return ''

  const parts = []

  if (style.fontWeight) parts.push('font-weight:700')
  if (style.fontStyle) parts.push('font-style:italic')
  if (style.textDecoration) parts.push('text-decoration:line-through')
  if (style.fontSize) parts.push(`font-size:${style.fontSize}`)
  if (style.fontFamily) parts.push(`font-family:${escapeHtml(style.fontFamily)}`)

  return parts.join(';')
}

function fieldDisplayHtml (value, config) {

  const display = formatFieldDisplay(value, config)

  if (!display) return null

  const text = escapeHtml(display.text)
  const css = styleAttribute(display.style)

  return css ? `<span style="${css}">${text}</span>` : text
}

function htmlWithTextStyle (innerHtml, config) {

  if (innerHtml == null) return innerHtml

  const css = styleAttribute(textStyle(config))

  if (!css) return innerHtml

  return `<span style="${css}">${innerHtml}</span>`
}

function applyValueFormatting (components, rows) {

  if (!components || !rows || rows.length === 0) return

  const columns = []

  for (const component of components) {

    const formatting = component && (component.valueFormatting || component._resolvedValueFormatting)

    if (formatting && formatting.type) {

      columns.push({ uniqueTitle: component.uniqueTitle, valueFormatting: formatting })
    }
  }

  if (!columns.length) return

  for (const row of rows) {

    for (const column of columns) {

      const original = row[column.uniqueTitle]

      if (original === null || original === undefined) continue
      if (readMultiselectItems(original)) continue

      const formatted = formatValue(original, column.valueFormatting)

      if (formatted !== original) row[column.uniqueTitle] = formatted
    }
  }
}

const SYSTEM_FIELD_TYPES = {
  id: 'number',
  createdAt: 'dateTime',
  updatedAt: 'dateTime'
}

function valueFormatKind (column) {

  if (!column) return null

  const type = column.type || SYSTEM_FIELD_TYPES[column.uniqueTitle]

  if (type === 'number') return 'number'
  if (type === 'text' || type === 'dateTime') return 'text'

  return null
}

function stampValueFormatting (field, column) {

  if (!field || !column) return
  if (field.type !== 'dataColumn' && field.type !== 'dataRichText') return
  if (field.valueFormatting && field.valueFormatting.type) return
  if (column.valueFormatting && column.valueFormatting.type) field.valueFormatting = column.valueFormatting
}

function assignViewValueFormatting (component, formatting) {

  if (!component) return

  if (formatting && formatting.type) component.valueFormatting = formatting
  else delete component.valueFormatting
}

module.exports = {
  CURRENCIES,
  FONT_FAMILIES,
  VALUE_FORMAT_OPTIONS,
  formatNumber,
  formatText,
  formatValue,
  formatFieldDisplay,
  fieldDisplayHtml,
  htmlWithTextStyle,
  textStyle,
  applySeparators,
  stripHtmlTags,
  applyValueFormatting,
  stampValueFormatting,
  assignViewValueFormatting,
  valueFormatKind
}
